/**
 * FilterTable — bảng dữ liệu nhỏ theo skill `/table-style` cho các tab Production
 * Report (Comparison, Item, …). Dùng `<Table>` thường (KHÔNG TableVirtuoso) vì
 * dataset per-PO nhỏ; vẫn đủ: FilterBar Search global + per-column ▽ filter +
 * sort click header + resize width (ResizeHandle + useColumnWidths) +
 * wrap-on-resize auto-height (§7.8). Cell render qua `col.render` (giữ số kế toán).
 *
 * Scroll wrapper hệ GRAY (`.app-dataframe`) — KHÔNG dùng class legacy
 * `app-table-wrap` (stripe teal/gold) vì làm cột freeze (`.app-sticky-col`) lệch
 * format với cột non-freeze (§7.6).
 */
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'

import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { getTableSortSuffix, sortTableRows, toggleTableSort, type TableSortKind, type TableSortState } from '@/utils/tableSort'

const HEADER_FREEZE_BG = 'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)'

export type FilterCol<R> = {
  key: string
  label: string
  width: number
  align?: 'right'
  frozen?: boolean
  /** numeric → filter min/max + sort số; mặc định text. */
  numeric?: boolean
  filter?: ColumnFilterKind
  /** getter cho search / filter / sort. */
  value: (row: R) => string | number | null
  /** render cell (mặc định = chuỗi value). */
  render?: (row: R) => ReactNode
}

export function FilterTable<R>({
  storageKey,
  columns,
  rows,
  rowKey,
  searchPlaceholder,
  footer,
}: {
  storageKey: string
  columns: FilterCol<R>[]
  rows: R[]
  rowKey: (row: R, index: number) => string | number
  searchPlaceholder?: string
  /** Footer (vd dòng TOTAL) tính trên rows ĐÃ filter/sort. */
  footer?: (rows: R[]) => ReactNode
}) {
  const { getWidth, startResize } = useColumnWidths(storageKey)
  const [searchTerm, setSearchTerm] = useState('')
  const searchPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: string; position: ColumnFilterAnchorPosition } | null>(null)
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  // Map row → record (col.key→value) cho search/filter/sort; giữ index gốc.
  const records = useMemo(() => {
    return rows.map((r, i) => {
      const rec: Record<string, unknown> = { __i: i }
      for (const c of columns) rec[c.key] = c.value(r)
      return rec
    })
  }, [rows, columns])

  const filterCols = useMemo(
    () => columns.map((c) => ({ key: c.key, label: c.label, kind: (c.filter ?? (c.numeric ? 'number' : 'text')) as ColumnFilterKind })),
    [columns],
  )
  const sortCols = useMemo(
    () => columns.map((c) => ({ key: c.key, kind: (c.numeric ? 'number' : 'text') as TableSortKind })),
    [columns],
  )

  const displayRows = useMemo(() => {
    const filtered = records.filter(
      (rec) => rowMatchesSearch(rec, searchTerm, filterCols) && rowMatchesAllColumnFilters(rec, columnFilters.filters),
    )
    const sorted = sortTableRows(filtered, sortState, sortCols)
    return sorted.map((rec) => rows[rec.__i as number])
  }, [records, rows, searchTerm, filterCols, columnFilters.filters, sortState, sortCols])

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAll = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    searchPopover.close()
  }

  const popCol = colPopover ? filterCols.find((c) => c.key === colPopover.key) ?? null : null
  const popKind = popCol ? inferColumnFilterKind(popCol, records) : 'text'
  const popOptions = popKind === 'checkboxes' && colPopover ? distinctColumnValues(records, colPopover.key) : undefined
  const popLabel = colPopover ? columns.find((c) => c.key === colPopover.key)?.label ?? '' : ''

  return (
    <Box>
      <FilterBar hasActiveFilters={hasActiveFilters} onClearAll={clearAll} activeFilterCount={activeFilterCount}>
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => searchPopover.open('search', e)} />
      </FilterBar>
      <Box className="app-dataframe app-dataframe--compact">
        <Box sx={{ overflow: 'auto', bgcolor: '#fff' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', '& tbody td': { verticalAlign: 'top' } }}>
            <colgroup>
              {columns.map((c) => (
                <col key={c.key} style={{ width: getWidth(c.key) ?? c.width }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {columns.map((c) => {
                  const w = getWidth(c.key) ?? c.width
                  const style: CSSProperties = { width: w, minWidth: w, maxWidth: w, position: 'relative' }
                  if (c.frozen) {
                    style.position = 'sticky'
                    style.left = 0
                    style.zIndex = 7
                    style.background = '#fff9f4'
                    style.backgroundImage = HEADER_FREEZE_BG
                  }
                  return (
                    <TableCell
                      key={c.key}
                      align={c.align}
                      className="app-table-header-cell"
                      onClick={() => setSortState((s) => toggleTableSort(s, c.key))}
                      style={style}
                      sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}
                    >
                      {c.label}
                      {getTableSortSuffix(sortState, c.key)}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(c.key)}
                        onClick={(position) => setColPopover({ key: c.key, position })}
                        ariaLabel={`Filter ${c.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(c.key, e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row, i) => (
                <TableRow key={rowKey(row, i)} hover>
                  {columns.map((c) => {
                    const w = getWidth(c.key) ?? c.width
                    // wrap khi cột text HOẶC user đã resize cột (§7.8 — auto-height).
                    const wrap = !c.numeric || getWidth(c.key) != null
                    return (
                      <TableCell
                        key={c.key}
                        align={c.align}
                        className={c.frozen ? 'app-sticky-col' : undefined}
                        style={{ width: w, minWidth: w, maxWidth: w }}
                        sx={{ py: 0.5, ...(wrap ? { whiteSpace: 'normal', overflowWrap: 'anywhere' } : { whiteSpace: 'nowrap' }) }}
                      >
                        {c.render ? c.render(row) : <Box component="span" className="app-table-cell-wrap">{String(c.value(row) ?? '')}</Box>}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
              {footer ? footer(displayRows) : null}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <FilterPopover
        open={searchPopover.state !== null}
        anchorEl={searchPopover.state?.anchor ?? null}
        onClose={() => searchPopover.close()}
      >
        {searchPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={searchPlaceholder ?? 'Search…'}
            onClear={() => searchPopover.close()}
          />
        )}
      </FilterPopover>

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popLabel}
        kind={popKind === 'none' ? 'text' : popKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => {
          if (colPopover) columnFilters.setFilter(colPopover.key, v)
        }}
        options={popOptions}
      />
    </Box>
  )
}
